import { type FC } from "react";

import { useMutation } from "@apollo/client/react";

import { type Project, ProjectArchiveDocument } from "@app_schema";

import { Button } from "@styled/button";
import { Modal } from "@styled/modal";
import { ModalClose } from "@styled/modal_close";
import { ModalContent } from "@styled/modal_content";
import { ModalFooter } from "@styled/modal_footer";
import { ModalHeader } from "@styled/modal_header";
import { ModalTitle } from "@styled/modal_title";

// Reversible-action confirmation modal. Composes the same `Modal*` primitives
// `Dialog` uses, but with non-destructive copy + an indigo confirm button —
// `Dialog` hardcodes "Are you sure you want to delete…" + a rose button,
// which would mislead clinicians here (archive is reversible).
export const ProjectArchiveDialog: FC<{
  project: Pick<Project, "id" | "name">;
  onArchive?(): void;
  onClose(): void;
}> = ({ project, onArchive, onClose }) => {
  // Refetch by query name covers every active `DashboardProjectsList`
  // regardless of tab/page variables — Apollo normalizes the project's own
  // fields, not the paginated `entries` array, so without this the archived
  // project would stay visible on the Active tab until a manual refresh.
  const [execute, { loading }] = useMutation(ProjectArchiveDocument, {
    refetchQueries: ["DashboardProjectsList", "InboxStatusCounts"],
    awaitRefetchQueries: true,
  });

  const onConfirm = async () => {
    const result = await execute({ variables: { id: project.id } });
    // Apollo's MutateResult exposes `error` (singular). If the mutation
    // failed, leave the dialog open so the user can retry rather than
    // silently dropping them back to a stale list.
    if (result.error) return;
    onClose();
    onArchive?.();
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader>
        <ModalTitle>Archive “{project.name}”?</ModalTitle>
        <ModalClose onClose={onClose} />
      </ModalHeader>
      <ModalContent>
        “{project.name}” will move to the Archived tab. You can unarchive it any
        time from the same menu — no data is deleted.
      </ModalContent>
      <ModalFooter>
        <Button type="button" color="slate" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          color="indigo"
          onClick={onConfirm}
          loading={loading}
        >
          Archive
        </Button>
      </ModalFooter>
    </Modal>
  );
};
